<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <SidebarMenuButton
        size="lg"
        class="tw-data-[state=open]:tw-bg-sidebar-accent tw-data-[state=open]:tw-text-sidebar-accent-foreground"
      >
        <div
          class="tw-flex tw-aspect-square tw-size-8 tw-items-center tw-justify-center tw-rounded-lg tw-bg-sidebar-primary tw-text-sidebar-primary-foreground"
        >
          <template v-if="selectedTenant?.logo">
            <img :src="selectedTenant.logo" :alt="selectedTenant.name" class="tw-size-4" />
          </template>
          <template v-else>
            <Building2 class="tw-size-4" />
          </template>
        </div>
        <div class="tw-grid tw-flex-1 tw-text-left tw-text-sm tw-leading-tight">
          <span class="tw-truncate tw-font-semibold">
            {{
              selectedTenant?.display_name ||
              selectedTenant?.name ||
              t('auth.titles.select_workspace')
            }}
          </span>
          <span v-if="availableTenants.length > 1" class="tw-truncate tw-text-xs tw-text-gray-400">
            {{ availableTenants.length }} {{ t('sidebar.titles.workspaces') }}
          </span>
        </div>
        <ChevronsUpDown v-if="availableTenants.length > 1" class="tw-ml-auto" />
      </SidebarMenuButton>
    </DropdownMenuTrigger>

    <DropdownMenuContent
      v-if="availableTenants.length > 1"
      class="tw-w-[--radix-dropdown-menu-trigger-width] tw-min-w-56 tw-rounded-lg"
      side="bottom"
      align="start"
      :side-offset="4"
    >
      <DropdownMenuLabel class="tw-text-xs tw-text-muted-foreground">
        {{ t('sidebar.titles.switch_workspace') }}
      </DropdownMenuLabel>
      <DropdownMenuItem
        v-for="tenant in availableTenants"
        :key="tenant.id"
        class="tw-gap-2 tw-p-2 tw-cursor-pointer"
        :class="{ 'tw-bg-accent': tenant.id === selectedTenant?.id }"
        @click="onTenantSwitch(tenant.id)"
      >
        <div class="tw-flex tw-size-6 tw-items-center tw-justify-center tw-rounded-sm tw-border">
          <template v-if="tenant.logo">
            <img :src="tenant.logo" :alt="tenant.name" class="tw-size-4 tw-shrink-0" />
          </template>
          <template v-else>
            <Building2 class="tw-size-4 tw-shrink-0" />
          </template>
        </div>
        <span class="tw-flex-1">{{ tenant.display_name || tenant.name }}</span>
        <Check v-if="tenant.id === selectedTenant?.id" class="tw-size-4 tw-ml-auto" />
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { Building2, ChevronsUpDown, Check } from 'lucide-vue-next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import { useAuthStore } from '@/stores/auth.store';

const { t } = useI18n();

const { availableTenants, selectedTenant } = storeToRefs(useAuthStore());
const { switchTenant, fetchUserTenants } = useAuthStore();

const onTenantSwitch = async (tenantId: number) => {
  if (tenantId === selectedTenant.value?.id) return;
  await switchTenant(tenantId);
};

onMounted(async () => {
  // Fetch tenants if not already loaded
  if (availableTenants.value.length === 0) {
    await fetchUserTenants();
  }
});
</script>
